import React, { useState } from 'react';
import { Modal, Form as BsForm } from 'react-bootstrap';
import { Button } from '@/components/ui/button';
import { useToasts } from '../../../../../context/Toast';
import { useUserInfo } from '../../../context/UserInfo';
import { useMutationErrorHandler } from '../../../../../hooks/useMutationErrorHandler';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { toISODateOnly } from '../../../../../utils/dateUtils';
import {
  TimeBalanceWithdrawalType,
  useRequestTimeBalanceWithdrawalMutation,
} from '../../../__generated__/graphql';
import { DifferenceWithSign } from '../../DifferenceWithSign';

type Props = {
  show: boolean;
  onClose: () => void;
  // Available balance in minutes.
  availableMinutes: number;
  // Which destinations the HR admin allows for self-service withdrawal. At least
  // one is always true (the button is hidden otherwise).
  allowMonetary: boolean;
  allowCompensatory: boolean;
};

export const WithdrawTimeBalanceModal: React.FC<Props> = ({
  show,
  onClose,
  availableMinutes,
  allowMonetary,
  allowCompensatory,
}) => {
  const t = useNamespacedTranslation('components.timeBalancePanel.withdraw');
  const { addSuccess } = useToasts();
  const { id: employeeId } = useUserInfo();
  const errorHandler = useMutationErrorHandler();

  const defaultDestination = allowMonetary
    ? TimeBalanceWithdrawalType.ToOneTimeCompensation
    : TimeBalanceWithdrawalType.ToAbsenceAllowance;

  const [hours, setHours] = useState<string>('');
  const [destination, setDestination] =
    useState<TimeBalanceWithdrawalType>(defaultDestination);
  const [submitting, setSubmitting] = useState(false);

  // The absence category and payout rate are resolved on Salesforce from the HR
  // admin's config — the employee only picks the destination (when both allowed).
  const bothAllowed = allowMonetary && allowCompensatory;

  const [requestWithdrawal] = useRequestTimeBalanceWithdrawalMutation();

  const amountMinutes = Math.round(parseFloat(hours) * 60);
  const amountIsValid =
    !Number.isNaN(amountMinutes) &&
    amountMinutes > 0 &&
    amountMinutes <= availableMinutes;
  const canSubmit = amountIsValid && !submitting;

  const handleClose = () => {
    setHours('');
    setDestination(defaultDestination);
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    const today = toISODateOnly(new Date());
    try {
      const result = await requestWithdrawal({
        variables: {
          employeeId,
          day: today,
          input: {
            day: today,
            amount: amountMinutes,
            withdrawalType: destination,
          },
        },
        // A compensatory withdrawal creates an absence allowance, so refresh the
        // dashboard compensatory amounts too (the mutation only refetches the
        // time balance itself).
        refetchQueries: ['DashboardCompensatoryTime'],
        awaitRefetchQueries: true,
      });
      const error = result.data?.timeSheet?.requestTimeBalanceWithdrawal?.error;
      if (error) {
        throw new Error(error);
      }
      addSuccess(t('successToast'));
      handleClose();
    } catch (error) {
      errorHandler(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('title')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 d-flex align-items-center gap-2 color-gray-2">
          <span>{t('available')}</span>
          <DifferenceWithSign value={availableMinutes} withColor={false} />
        </div>

        <BsForm.Group className="mb-3">
          <BsForm.Label>{t('amountLabel')}</BsForm.Label>
          <BsForm.Control
            type="number"
            min={0}
            step={0.25}
            value={hours}
            placeholder={t('amountPlaceholder')}
            onChange={(e) => setHours(e.target.value)}
            isInvalid={hours !== '' && !amountIsValid}
          />
          <BsForm.Control.Feedback type="invalid">
            {t('amountError')}
          </BsForm.Control.Feedback>
        </BsForm.Group>

        {bothAllowed && (
          <BsForm.Group className="mb-3">
            <BsForm.Label>{t('destinationLabel')}</BsForm.Label>
            <BsForm.Check
              type="radio"
              id="withdraw-destination-compensation"
              name="withdraw-destination"
              label={t('destinationCompensation')}
              checked={
                destination === TimeBalanceWithdrawalType.ToOneTimeCompensation
              }
              onChange={() =>
                setDestination(TimeBalanceWithdrawalType.ToOneTimeCompensation)
              }
            />
            <BsForm.Check
              type="radio"
              id="withdraw-destination-absence"
              name="withdraw-destination"
              label={t('destinationAbsence')}
              checked={
                destination === TimeBalanceWithdrawalType.ToAbsenceAllowance
              }
              onChange={() =>
                setDestination(TimeBalanceWithdrawalType.ToAbsenceAllowance)
              }
            />
          </BsForm.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          {t('cancel')}
        </Button>
        <Button variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
          {t('submit')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
